import React from 'react';
import { Box, Button, Stack, Text } from '@chakra-ui/react';
import { OktonPanel, OktonTextarea } from '@okton/platform-ui';
import * as api from './aiPlanTextApi';
import { AiPlanTextHistory } from './AiPlanTextHistory';
import { PlanError } from './aiPlansApi';
type Props = api.TextIdentity & { token: string };
export const AiPlanTextPanel: React.FC<Props> = props => <TextWorkspace key={api.textStorageKey(props)} {...props} />;
const TextWorkspace: React.FC<Props> = props => {
 const key = api.textStorageKey(props);
 const initial = React.useMemo(() => { try { return { ids: api.loadTextRequest(key), error: '' }; } catch { return { ids: null, error: 'Não foi possível ler a solicitação salva neste navegador. O envio está bloqueado.' }; } }, [key]);
 const [ids, setIds] = React.useState<api.TextRequestIds | null>(initial.ids);
 const [history, setHistory] = React.useState<api.TextHistoryPage | null>(null);
 const [historyOffset, setHistoryOffset] = React.useState(0);
 const [historyLoading, setHistoryLoading] = React.useState(true);
 const [historyError, setHistoryError] = React.useState('');
 const [historyReload, setHistoryReload] = React.useState(0);
 const [input, setInput] = React.useState('');
 const [usage, setUsage] = React.useState<api.TextUsage | null>(null);
 const [generationEnabled, setGenerationEnabled] = React.useState<boolean | null>(null);
 const [availabilityError, setAvailabilityError] = React.useState('');
 const [result, setResult] = React.useState<api.TextExecution | null>(null);
 const [callNotFound, setCallNotFound] = React.useState(false);
 const [busyLabel, setBusyLabel] = React.useState('Consultando solicitação...');
 const [busy, setBusy] = React.useState(false);
 const [error, setError] = React.useState(initial.error);
 const [usageError, setUsageError] = React.useState('');
 const [lastChecked, setLastChecked] = React.useState<Date | null>(null);
 const [copyStatus, setCopyStatus] = React.useState('');
 const [showCopyFallback, setShowCopyFallback] = React.useState(false);
 const restored = React.useRef(false);
 const [reload, setReload] = React.useState(0);
 const alive = React.useRef(true); const sending = React.useRef(false);
 React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
 React.useEffect(() => {
  const controller = new AbortController(); setUsage(null); setUsageError(''); setGenerationEnabled(null); setAvailabilityError('');
  api.getTextAvailability(props.token, props, controller.signal).then(value => { if (!controller.signal.aborted) setGenerationEnabled(value); })
   .catch(() => { if (!controller.signal.aborted) setAvailabilityError('Não foi possível conferir a disponibilidade. Atualize para tentar novamente.'); });
  api.getTextUsage(props.token, props, controller.signal).then(value => { if (!controller.signal.aborted) setUsage(value); })
   .catch(reason => { if (!controller.signal.aborted) setUsageError(reason.message || 'Não foi possível atualizar o saldo.'); });
  return () => controller.abort();
 }, [props.token, key, reload]); // eslint-disable-line react-hooks/exhaustive-deps
 React.useEffect(() => {
  const controller = new AbortController();
  setHistoryLoading(true); setHistoryError(''); setHistory(null);
  api.getTextHistory(props.token, props, historyOffset, controller.signal).then(page => {
   if (!controller.signal.aborted) setHistory(page);
  }).catch(() => {
   if (!controller.signal.aborted) setHistoryError('Não foi possível carregar o histórico. O protocolo atual foi preservado. Atualize o histórico para tentar novamente.');
  }).finally(() => { if (!controller.signal.aborted) setHistoryLoading(false); });
  return () => controller.abort();
 }, [props.token, key, historyOffset, historyReload, reload]); // eslint-disable-line react-hooks/exhaustive-deps
 const run = async (recover: boolean, selectedIds?: api.TextRequestIds) => {
  if (sending.current || initial.error || (!recover && (generationEnabled !== true || ids || !usage || usage.availableMicros === 0 || !input.trim() || api.textBytes(input) > 16384))) return;
  sending.current = true; setBusyLabel(recover ? 'Consultando solicitação...' : 'Enviando solicitação...'); setBusy(true); setError(''); setCopyStatus(''); setCallNotFound(false);
  try {
   let selected = selectedIds || ids;
   if (!recover) {
    selected = api.newTextRequest(); api.saveTextRequest(key, selected); setIds(selected);
   }
   if (!selected) return;
   const value = recover ? await api.recoverText(props.token, props, selected) : await api.executeText(props.token, props, selected, input);
   if (alive.current) { setResult(value); setLastChecked(new Date()); }
  } catch (reason) {
   if (alive.current) {
    setCallNotFound(recover && reason instanceof PlanError && reason.status === 404);
    setError(recover
     ? 'Não foi possível atualizar a situação. O protocolo foi preservado. Tente atualizar novamente; isso não reenvia a geração.'
     : reason instanceof Error ? reason.message : 'Não foi possível confirmar a resposta. Atualize a situação para consultar o mesmo protocolo.');
   }
  } finally { if (alive.current) { sending.current = false; setBusy(false); setReload(value => value + 1); } }
 };
 React.useEffect(() => {
  // One recovery of the saved protocol per workspace. Never calls executeText.
  if (initial.ids && !initial.error && !restored.current) {
   restored.current = true;
   void run(true);
  }
 }, []); // eslint-disable-line react-hooks/exhaustive-deps
 const selectHistory = async (selected: api.TextRequestIds) => {
  if (sending.current || initial.error || (ids && !result)) return;
  try {
   api.saveTextRequest(key, selected);
   setIds(selected); setResult(null); setError(''); setCallNotFound(false); setLastChecked(null);
   setCopyStatus(''); setShowCopyFallback(false);
   await run(true, selected);
  } catch { setError('Não foi possível abrir a solicitação. O protocolo anterior foi preservado.'); }
 };
 const reset = () => {
  try { window.sessionStorage.removeItem(key); setIds(null); setInput(''); setResult(null); setCallNotFound(false); setError(''); setLastChecked(null); setCopyStatus(''); setShowCopyFallback(false); }
  catch { setError('Não foi possível preparar uma nova solicitação neste navegador.'); }
 };
 const cancel = async () => {
  if (!ids || sending.current || (!callNotFound && result?.call.state !== 'reserved')) return;
  sending.current = true; setBusyLabel('Encerrando solicitação...'); setBusy(true); setError('');
  let refreshAfterConflict = false;
  try {
   await api.cancelText(props.token, props, ids);
   if (alive.current) { reset(); setReload(value => value + 1); }
  } catch (reason) {
   if (alive.current) {
    setCallNotFound(false);
    setResult(null); setLastChecked(null);
    refreshAfterConflict = reason instanceof PlanError && reason.status === 409;
    setError(reason instanceof PlanError && reason.status === 409
     ? 'Não foi possível encerrar esta solicitação no estado atual. Consulte o resultado; se estiver em conferência, será necessária conciliação.'
     : reason instanceof Error ? reason.message : 'Não foi possível confirmar o encerramento.');
   }
  } finally { if (alive.current) { sending.current = false; setBusy(false); } }
  if (alive.current && refreshAfterConflict) await run(true);
 };
 const completed = result?.call.state === 'settled' || result?.call.state === 'released';
 const statuses: Record<string, string> = {
  reserved: 'Aguardando confirmação do envio',
  dispatched: 'Aguardando resposta',
  uncertain: 'Aguardando conferência da Okton',
  settled: 'Solicitação concluída',
  released: 'Solicitação encerrada',
 };
 const explanations: Record<string, string> = {
  reserved: 'Há saldo reservado, mas o envio ainda não foi confirmado. Você pode atualizar a situação ou encerrar se ainda não foi enviada.',
  dispatched: 'A solicitação foi enviada e ainda não há uma resposta confirmada. Atualizar a situação consulta o mesmo protocolo, sem gerar outra cobrança.',
  uncertain: 'Não foi possível confirmar a resposta. O valor permanece reservado para conciliação e ainda não é consumo confirmado. Você pode iniciar outro pedido; este protocolo continuará no histórico aguardando conferência administrativa.',
  settled: 'O consumo apurado foi registrado e o restante da reserva foi liberado.',
  released: 'A reserva foi liberada. Você pode iniciar uma nova solicitação.',
 };
 const summary = ids ? [
  'Ocorrência — Assistente de texto Okton',
  'Empresa/filial: ' + (props.context.branchName || props.context.branchId),
  'Empresa: ' + props.context.companyId + ' | Filial: ' + props.context.branchId,
  'Protocolo: ' + ids.callId,
  'Solicitação: ' + ids.requestId,
  'Situação: ' + (result ? statuses[result.call.state] : 'Ainda não confirmada'),
  ...(result && !completed ? ['Valor reservado: ' + api.textMoney(result.call.estimate.brlMicros)] : []),
 ].join('\n') : '';
 const copySummary = async () => {
  try {
   if (!navigator.clipboard?.writeText) throw new Error();
   await navigator.clipboard.writeText(summary);
   if (alive.current) { setCopyStatus('Dados copiados. Você pode compartilhá-los com o suporte da Okton.'); setShowCopyFallback(false); }
  } catch {
   if (alive.current) { setCopyStatus('A cópia automática não está disponível. Selecione e copie os dados abaixo.'); setShowCopyFallback(true); }
  }
 };
 const outcomes: Record<string, string> = { empty: 'A solicitação terminou sem texto.', refused: 'Não foi possível atender a esse pedido.', incomplete: 'A resposta ficou incompleta.', failed: 'A geração não foi concluída.' };
 return <OktonPanel title="Assistente de texto" subtitle="Trabalha com o texto informado, sem consultar ou alterar dados do ERP.">
  <Stack spacing="4">
   <Text>{props.context.branchName || 'Filial ' + props.context.branchId}</Text>
   {usage ? <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
    <Text>Saldo disponível: <strong>{api.textMoney(usage.availableMicros)}</strong></Text>
    <Text>Consumido: {api.textMoney(usage.confirmedMicros)}</Text>
    <Text>Reservado: {api.textMoney(usage.reservedMicros)}</Text>
    <Text>Em conferência: {api.textMoney(usage.pendingMicros)}</Text>
   </Stack> : <Text>{usageError || 'Consultando saldo...'}</Text>}
   {!ids && <Text role="status">{availabilityError || (generationEnabled === null ? 'Conferindo disponibilidade...' : generationEnabled ? 'Geração habilitada. O envio continua sujeito ao saldo e aos limites vigentes.' : 'A geração está temporariamente desativada. Você pode consultar ou encerrar solicitações anteriores.')}</Text>}
   {!ids && <Button alignSelf="flex-start" size="sm" variant="outline" isDisabled={busy} onClick={() => setReload(value => value + 1)}>Atualizar saldo e disponibilidade</Button>}
   {!ids && <><OktonTextarea name="ai-plan-text" label="O que você precisa?" value={input} rows={5} isDisabled={busy || Boolean(initial.error)} onChange={event => setInput(event.target.value)} />
    <Text fontSize="sm">{api.textBytes(input)} de 16.384 bytes</Text>
    <Text fontSize="sm">O envio depende de saldo para a reserva máxima. Ao concluir, apenas o consumo apurado permanece descontado.</Text>
    <Button colorScheme="blue" alignSelf="flex-start" isLoading={busy} isDisabled={generationEnabled !== true || !usage || usage.availableMicros === 0 || !input.trim() || api.textBytes(input) > 16384 || Boolean(initial.error)} onClick={() => void run(false)}>Enviar solicitação</Button></>}
   {ids && <>
    <Box borderWidth="1px" borderRadius="md" p="4">
     <Text role="status" fontWeight="semibold">{result ? statuses[result.call.state] : busy ? busyLabel : 'Situação ainda não confirmada'}</Text>
     <Text mt="2">{result ? explanations[result.call.state] : callNotFound
      ? 'O protocolo não foi localizado. Use “Encerrar se não enviada” para confirmar o encerramento antes de iniciar outra solicitação.'
      : 'O protocolo está salvo neste navegador. Atualize a situação antes de iniciar outra solicitação.'}</Text>
     {result?.call.state === 'uncertain' && <Text mt="2" fontSize="sm">Atualizar consulta apenas a situação da conferência; não reenvia a geração e não libera o valor automaticamente.</Text>}
    </Box>
    <Text fontSize="sm" wordBreak="break-all">Protocolo: {ids.callId}</Text>
    {result && !completed && <Text>{result.call.state === 'uncertain' ? 'Valor desta solicitação em conferência:' : 'Reserva desta solicitação:'} {api.textMoney(result.call.estimate.brlMicros)}</Text>}
    {result?.call.actual && <Text>Consumo desta solicitação: {api.textMoney(result.call.actual.brlMicros)}</Text>}
    {result?.result?.text && <Box whiteSpace="pre-wrap" overflowWrap="anywhere" role="region" aria-label="Resposta do assistente">{result.result.text}</Box>}
    {result?.result && outcomes[result.result.outcome] && <Text>{outcomes[result.result.outcome]}</Text>}
    <Stack direction={{ base: 'column', sm: 'row' }}>
     <Button variant="outline" isLoading={busy} loadingText={busyLabel} onClick={() => void run(true)}>Atualizar situação</Button>
     <Button variant="ghost" isDisabled={busy} onClick={() => void copySummary()}>Copiar dados da ocorrência</Button>
     {(callNotFound || result?.call.state === 'reserved') && <Button isDisabled={busy} variant="ghost" onClick={() => void cancel()}>Encerrar se não enviada</Button>}
     {(completed || result?.call.state === 'uncertain') && <Button isDisabled={busy || (result?.call.state === 'uncertain' && (!history || historyLoading || Boolean(historyError)))} onClick={reset}>Nova solicitação</Button>}
    </Stack>
    {lastChecked && <Text fontSize="sm">Última consulta: {lastChecked.toLocaleString('pt-BR')}</Text>}
    {copyStatus && <Text role="status" fontSize="sm">{copyStatus}</Text>}
    {showCopyFallback && <Box as="pre" whiteSpace="pre-wrap" overflowWrap="anywhere" userSelect="text" tabIndex={0} aria-label="Dados da ocorrência">{summary}</Box>}
   </>}
   {error && <Text role="alert" color="red.500">{error}</Text>}
   <AiPlanTextHistory page={history} error={historyError} loading={historyLoading} offset={historyOffset}
    disabled={busy || Boolean(initial.error) || Boolean(ids && !result)}
    onRefresh={() => setHistoryReload(value => value + 1)} onPage={setHistoryOffset}
    onSelect={selected => void selectHistory(selected)} />
  </Stack>
 </OktonPanel>;
};
